<template>
  <div class="info">
    <div class="title">
      <img class="title-icon" src="@/common/image/assist/inspire.svg" alt="" />
      <span class="title-hd" v-html="topicSentence"></span>
    </div>
    <div class="keywords" v-if="topicDetail.phrases.length > 0">
      <div
        class="keywords-wrap"
        :style="lang === 'CN' ? 'width:95px' : 'width:130px'"
      >
        {{ $t('assist.topicKeywords') }}
      </div>
      <div class="keywords-block">
        <span
          class="keywords-item"
          v-for="(item, index) in topicDetail.phrases"
          :key="index"
        >
          {{ item }}
        </span>
      </div>
    </div>
    <div class="abbrev" v-if="abbrev.toString()">
      <div
        class="abbrev-wrap"
        :style="lang === 'CN' ? 'width:80px' : 'width:105px'"
      >
        {{ $t('assist.abbreviation') }}
      </div>
      <div class="abbrev-item">{{ abbrev.join('; ') }}</div>
    </div>
    <div class="anaphor" v-if="anaphorConList.length > 0">
      <div
        class="anaphor-title"
        :style="lang === 'CN' ? 'width:80px' : 'width:105px'"
      >
        {{ $t('assist.coreference') }}
      </div>
      <div class="anaphor-content">
        <div
          v-for="(item, index) in anaphorConList"
          :key="index"
          v-html="item"
        ></div>
      </div>
    </div>
    <div class="eval" v-if="topicDetail.credibilityValue">
      <div
        class="eval-wrap"
        :style="lang === 'CN' ? 'width:80px' : 'width:105px'"
      >
        {{ $t('assist.evaluation') }}
      </div>
      <div class="eval-level">
        <el-popover
          placement="bottom-start"
          :width="lang === 'CN' ? 400 : 568"
          trigger="hover"
          :show-arrow="false"
          popper-class="popper-eval"
        >
          <div class="eval-bd">
            <div class="eval-bd-title">
              {{ $t('assist.credibility.title') }}
            </div>
            <div
              class="eval-bd-main"
              v-html="$t('assist.credibility.exceptionalExp')"
            ></div>
            <div
              class="eval-bd-main"
              v-html="$t('assist.credibility.goodExp')"
            ></div>
            <div
              class="eval-bd-main"
              v-html="$t('assist.credibility.acceptibleExp')"
            ></div>
            <div
              class="eval-bd-main"
              v-html="$t('assist.credibility.marginalExp')"
            ></div>
            <div :class="lang === 'CN' ? 'eval-state-cn' : 'eval-state-en'">
              {{ $t('assist.credibility.anouncement') }}
            </div>
          </div>
          <template #reference>
            <div style="display: flex">
              <span>{{ $t('assist.credibile') }}</span>
              <img
                class="credibility"
                src="@/common/image/assist/credible.svg"
                alt=""
              />
            </div>
          </template>
        </el-popover>
      </div>
      <div style="margin-left: 15px">
        <div v-if="topicDetail.credibilityValue === 1">
          <img src="@/common/image/assist/credible-layer1.svg" alt="" />
          <span style="margin-left: 15px">{{
            $t('assist.credibility.marginal')
          }}</span>
        </div>
        <div v-else-if="topicDetail.credibilityValue === 2">
          <img src="@/common/image/assist/credible-layer2.svg" alt="" />
          <span style="margin-left: 15px">{{
            $t('assist.credibility.acceptible')
          }}</span>
        </div>
        <div v-else-if="topicDetail.credibilityValue === 3">
          <img src="@/common/image/assist/credible-layer3.svg" alt="" />
          <span style="margin-left: 15px">{{
            $t('assist.credibility.good')
          }}</span>
        </div>
        <div v-else-if="topicDetail.credibilityValue === 4">
          <img src="@/common/image/assist/credible-layer4.svg" alt="" />
          <span style="margin-left: 15px">{{
            $t('assist.credibility.exceptional')
          }}</span>
        </div>
      </div>
    </div>
    <el-divider />
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, PropType, ref } from 'vue'
import { topicDetail } from '@/api/assist'
import { getLang } from '@/hooks/lang'

export default defineComponent({
  props: {
    topicDetail: {
      type: Object as PropType<topicDetail>,
      required: false,
      default: () => ({}),
    },
  },
  setup(props) {
    const abbrev = ref([] as string[])
    const lang = getLang()
    const topicSentence = ref<string>('')
    const anaphorConList = ref<string[]>([])

    const anaphorHandle = () => {
      const anaphorIndexList = props.topicDetail.anaphorList
      if (anaphorIndexList.length > 0) {
        for (let i = 0; i <= anaphorIndexList.length - 1; i++) {
          let lastIndex = 0
          const posIndex = `<span class='anaphora'>${i + 1}</span>`
          const anaphor = `<span style='color:#444444'>${anaphorIndexList[i].anaphor}</span>`
          const antecedent = anaphorIndexList[i].antecedent
          const conLabel = `${anaphor}${posIndex} = ${antecedent}`
          anaphorConList.value.push(conLabel)

          if (i === 0) {
            lastIndex = anaphorIndexList[i].anaphorSpan[1]
            topicSentence.value =
              props.topicDetail.topicSentence.slice(0, lastIndex) +
              posIndex +
              props.topicDetail.topicSentence.slice(lastIndex)
          } else {
            lastIndex = anaphorIndexList[i].anaphorSpan[1] + 31 * i
            topicSentence.value =
              topicSentence.value.slice(0, lastIndex) +
              posIndex +
              topicSentence.value.slice(lastIndex)
          }
        }
      } else {
        topicSentence.value = props.topicDetail.topicSentence
      }
    }

    const abbrevHandle = () => {
      const abbrevMap = props.topicDetail.abbreviations
      for (const key in abbrevMap) {
        abbrev.value.push(abbrevMap[`${key}`].concat(` ( ${key} )`))
      }
    }

    onMounted(() => {
      anaphorHandle()
      abbrevHandle()
    })
    return {
      abbrev,
      lang,
      topicSentence,
      anaphorConList,
    }
  },
})
</script>

<style lang="less" scoped>
.info {
  margin: 0 auto;
  .title {
    display: flex;
    font-weight: 700;
    font-size: 24px;
    line-height: 30px;
    color: #303133;
    margin-bottom: 15px;
    .title-icon {
      margin-bottom: auto;
    }
    .title-hd {
      margin-left: 6px;
      color: #303133;
    }
  }
  .keywords {
    display: flex;
    align-items: flex-start;
    margin-left: 30px;
    margin-top: 15px;
    .keywords-wrap {
      font-weight: 600;
      font-size: 15px;
      color: #6b727f;
      margin-bottom: 10px;
    }
    .keywords-block {
      width: 645px;
      margin-top: -2px;
    }
    .keywords-item {
      color: #6b727f;
      background: #f5f7fa;
      margin-right: 10px;
      margin-bottom: 10px;
      padding: 3px 8px;
      border: 1px solid #e5e5e5;
      border-radius: 10px;
      font-size: 12px;
      height: 25px;
      display: inline-block;
    }
  }
  .abbrev {
    display: flex;
    margin-left: 30px;
    .abbrev-wrap {
      font-weight: 600;
      font-size: 15px;
      color: #6b727f;
    }
    .abbrev-item {
      color: #444444;
      font-size: 15px;
      margin-bottom: 10px;
    }
  }
  .anaphor {
    display: flex;
    margin-left: 30px;
    padding-bottom: 5px;
    .anaphor-title {
      line-height: 25px;
      font-weight: 600;
      font-size: 15px;
      color: #6b727f;
    }
    .anaphor-content {
      max-width: 660px;
      font-size: 15px;
      color: #444444;
      div {
        line-height: 25px;
      }
    }
  }
  .eval {
    display: flex;
    align-items: center;
    font-weight: 600;
    font-size: 15px;
    margin-left: 30px;
    padding-bottom: 10px;
    .eval-wrap {
      color: #6b727f;
    }
    .eval-level {
      color: #444444;
      .credibility {
        margin-top: 4px;
        margin-left: 5px;
      }
    }
  }
  .el-divider {
    margin-top: 30px;
    background: 0;
    border-top: 1px solid #e5e5e5;
  }
}

.mobile-viewport {
  .keywords .keywords-block {
    width: auto;
  }
  .info .title .title-hd {
    font-size: 17px;
  }
  .keywords,
  .abbrev,
  .anaphor,
  .eval {
    flex-wrap: wrap;
  }
  .info .abbrev .abbrev-wrap {
    width: 95px;
    margin-bottom: 10px;
  }
}
</style>
<style>
.anaphora {
  width: 15px;
  height: 15px;
  line-height: 14px;
  display: inline-block;
  font-size: 12px;
  border: 1px solid #000;
  border-radius: 15px;
  text-align: center;
  vertical-align: top;
  transform: scale(0.8);
}
.popper-eval .eval-bd {
  font-size: 12px;
  margin: -10px 0px;
  word-break: break-word;
}
.popper-eval .eval-bd .eval-bd-title {
  font-weight: 700;
  color: #444444;
  height: auto;
}
.popper-eval .eval-bd .eval-bd-main {
  height: 20px;
  line-height: 20px;
}
.popper-eval .eval-bd .eval-state-cn {
  transform: scale(0.82);
  margin-left: -40px;
  margin-right: -36px;
  color: #c0c4cc;
  line-height: 20px;
}
.popper-eval .eval-bd .eval-state-en {
  transform: scale(0.82);
  margin-left: -55px;
  margin-right: -50px;
  color: #c0c4cc;
  line-height: 20px;
}
</style>
